/**
 * oracleBackendClient — talks to the hosted-Claude backend (/api/oracle).
 * -------------------------------------------------------------------------
 * Sends a conversation to the server-side proxy and consumes the SSE stream it
 * returns, accumulating the reply text. The Anthropic API key never touches the
 * browser — this only ever speaks to the same-origin /api/oracle endpoint (see
 * scripts/vite-oracle-proxy.mjs).
 *
 * When the backend isn't there (no key configured → 503, or a production build
 * with no dev middleware → the SPA's index.html), we throw OracleBackend
 * Unavailable so the Oracle router can fall back to its offline catalogue
 * instead of surfacing an error. Any other failure (a 5xx from Anthropic mid-
 * request) is also treated as "unavailable" so the user still gets an answer.
 */

import { apiUrl } from './apiBase.js';

/** Signals the caller should fall back — the backend can't serve this. */
export class OracleBackendUnavailable extends Error {
  constructor(message) {
    super(message);
    this.name = 'OracleBackendUnavailable';
  }
}

/** Parse one raw SSE record ("event: x\ndata: y") into { event, data }. */
function parseSseRecord(raw) {
  let event = 'message';
  const dataLines = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  return { event, data: dataLines.join('\n') };
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * askOracleBackend — POST the conversation, stream back the reply.
 *
 * @param {Array<{role:'user'|'assistant', content:string}>} messages
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {(fullText:string)=>void} [options.onDelta]  called with the running
 *        text each time a chunk arrives (drives the live-typing bubble)
 * @returns {Promise<{text:string, model:string|null}>}
 * @throws {OracleBackendUnavailable} when the backend can't serve the request
 */
export async function askOracleBackend(messages, { signal, onDelta } = {}) {
  let res;
  try {
    res = await fetch(apiUrl('/api/oracle'), {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ messages }),
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new OracleBackendUnavailable(`network: ${err?.message ?? 'fetch failed'}`);
  }

  // 503 = no key configured; 404/501 = no endpoint (prod build). All → fall back.
  if (res.status === 503 || res.status === 404 || res.status === 501) {
    throw new OracleBackendUnavailable(`backend ${res.status}`);
  }
  // A production build with no /api/oracle serves index.html (200 text/html) —
  // that isn't a stream, so treat any non-event-stream response as unavailable.
  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok || !res.body || !contentType.includes('text/event-stream')) {
    throw new OracleBackendUnavailable(`backend ${res.status} (${contentType || 'no body'})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let model = null;
  let streamError = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) >= 0) {
      const raw = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      if (!raw.trim()) continue;

      const { event, data } = parseSseRecord(raw);
      if (!data) continue;
      const payload = safeParse(data);
      if (!payload) continue;

      if (event === 'delta' && typeof payload.text === 'string') {
        text += payload.text;
        onDelta?.(text);
      } else if (event === 'done') {
        model = payload.model ?? model;
      } else if (event === 'error') {
        streamError = payload.message ?? 'stream error';
      }
    }
  }

  // Mid-stream failure with nothing usable → fall back. If we already have text,
  // keep it (a partial answer beats none).
  if (streamError && !text.trim()) throw new OracleBackendUnavailable(streamError);

  return { text: text.trim(), model };
}
